/**
 * Farbhilfen für Aufgabenprüfungen.
 *
 * Word liefert Farben je nach Eigenschaft als Hex-Wert (`#FF0000`) oder als
 * Name (`Yellow`). Für die Kursaufgaben reicht es zu wissen, in welcher
 * Farbfamilie ein Wert liegt – so wird auch ein etwas dunkleres Rot aus der
 * Farbpalette als "rot" akzeptiert.
 */

export type ColorFamily =
  | "rot"
  | "orange"
  | "gelb"
  | "grün"
  | "türkis"
  | "blau"
  | "lila"
  | "pink"
  | "schwarz"
  | "grau"
  | "weiß";

const NAMED_COLORS: Record<string, string> = {
  black: "#000000",
  blue: "#0000FF",
  brightgreen: "#00FF00",
  darkblue: "#00008B",
  darkred: "#8B0000",
  darkyellow: "#808000",
  gray: "#808080",
  green: "#008000",
  grey: "#808080",
  pink: "#FF00FF",
  red: "#FF0000",
  teal: "#008080",
  turquoise: "#40E0D0",
  violet: "#800080",
  white: "#FFFFFF",
  yellow: "#FFFF00",
};

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

export function parseColor(value: string | null | undefined): Rgb | null {
  if (!value) return null;
  const raw = value.trim();
  if (!raw || raw.toLowerCase() === "none") return null;

  const named = NAMED_COLORS[raw.toLowerCase().replace(/\s+/g, "")];
  const hex = (named ?? raw).replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;

  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  };
}

/** Ordnet einen Farbwert einer gut unterscheidbaren Farbfamilie zu. */
export function colorFamily(value: string | null | undefined): ColorFamily | null {
  const rgb = parseColor(value);
  if (!rgb) return null;

  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lightness = (max + min) / 2;
  const delta = max - min;
  const saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));

  if (saturation < 0.15) {
    if (lightness < 0.2) return "schwarz";
    if (lightness > 0.9) return "weiß";
    return "grau";
  }

  let hue: number;
  if (max === r) hue = ((g - b) / delta) % 6;
  else if (max === g) hue = (b - r) / delta + 2;
  else hue = (r - g) / delta + 4;
  hue = (hue * 60 + 360) % 360;

  if (hue < 15 || hue >= 345) return "rot";
  if (hue < 45) return "orange";
  if (hue < 70) return "gelb";
  if (hue < 160) return "grün";
  if (hue < 200) return "türkis";
  if (hue < 260) return "blau";
  if (hue < 300) return "lila";
  return "pink";
}

export function isColorFamily(value: string | null | undefined, family: ColorFamily): boolean {
  return colorFamily(value) === family;
}

/**
 * Relative Leuchtdichte einer Farbe nach WCAG 2.1.
 *
 * Grundlage für den Kontrastwert. Die Gewichtung der drei Kanäle bildet ab,
 * dass das Auge Grün deutlich heller wahrnimmt als Blau.
 */
function relativeLuminance(rgb: Rgb): number {
  const kanal = (wert: number) => {
    const anteil = wert / 255;
    return anteil <= 0.03928 ? anteil / 12.92 : ((anteil + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * kanal(rgb.r) + 0.7152 * kanal(rgb.g) + 0.0722 * kanal(rgb.b);
}

/**
 * Kontrastverhältnis zweier Farben nach WCAG 2.1, zwischen 1 und 21.
 *
 * Die Barrierefreiheitsrichtlinien verlangen mindestens 4,5:1 für normalen
 * Text und 3:1 für großen Text ab 18 pt fett oder 24 pt normal – auf Folien
 * ist praktisch aller Text groß in diesem Sinne. `null`, wenn eine der beiden
 * Farben nicht lesbar ist.
 */
export function contrastRatio(
  vordergrund: string | null | undefined,
  hintergrund: string | null | undefined,
): number | null {
  const a = parseColor(vordergrund);
  const b = parseColor(hintergrund);
  if (!a || !b) return null;

  const heller = Math.max(relativeLuminance(a), relativeLuminance(b));
  const dunkler = Math.min(relativeLuminance(a), relativeLuminance(b));
  return (heller + 0.05) / (dunkler + 0.05);
}
